import React from 'react';
import { CommentLinkWrapper, UseCommentLinkProps } from './useCommentLink';
import classNames from 'classnames';
import DeferRender from '@/components/common/DeferRender';
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import FormatDate, { ExpandedDate } from '@/components/common/FormatDate';
import LWTooltip from "../../common/LWTooltip";

// The amount of time during which you can edit a comment, without it causing
// the comment to be marked as edited.
const EDIT_GRACE_PERIOD = 60*60*1000; //1hr

const styles = defineStyles("CommentsItemDate", (theme: ThemeType) => ({
  root: {
    marginLeft: 2,
    marginRight: 16,

    // Create a stacking context and set z-index to be higher than the vote
    // buttons, which are to the right of this and have a click-target that
    // partially overlaps.
    position: "relative",

    // Prevent permalink-icon and date from wrapping onto separate lines, in
    // narrow/flexbox contexts
    whiteSpace: "nowrap",

    // Pin the font size so the date renders consistently regardless of which
    // context wraps the comment (post page, /allPosts quick takes, frontpage
    // quick takes, comment permalink, etc., were inheriting different sizes
    // from their containers — 13px in some places, 15.08px in others).
    fontSize: 15.08,

    zIndex: theme.zIndexes.commentPermalinkIcon,
    color: theme.palette.text.dim,
  },
  answerDate: {
    color: "inherit",
  },
  editedMarker: {
    paddingLeft: 2,
    fontSize: 12,
  },
}));

type CommentsItemDateProps = UseCommentLinkProps & {
  comment: CommentsList,
  preventDateFormatting?: boolean,
  className?: string,
};

const CommentsItemDate = ({comment, preventDateFormatting, className, ...rest}: CommentsItemDateProps) => {
  const classes = useStyles(styles);
  
  let dateFormat: string | undefined;
  if (preventDateFormatting) {
    dateFormat = undefined;
  } else if (comment.answer) {
    dateFormat = "MMM DD, YYYY";
  } else if (comment.debateResponse) {
    dateFormat = "h:mm a";
  } else {
    dateFormat = undefined;
  }

  const linkContents = (<LWTooltip
    title={<CommentDateTooltip comment={comment}/>}
  >
    <FormatDate
      date={comment.postedAt}
      format={dateFormat}
      tooltip={false}
    />
    {markCommentAsEdited(comment) && <span className={classes.editedMarker}>{"*"}</span>}
  </LWTooltip>);
  
  return (
    <span className={classNames(
      classes.root,
      comment.answer && classes.answerDate,
      className,
    )}>
      <DeferRender ssr={false} fallback={linkContents}>
        <CommentLinkWrapper comment={comment} {...rest}>
          {linkContents}
        </CommentLinkWrapper>
      </DeferRender>
    </span>
  );
}

const markCommentAsEdited = (comment: CommentsList): boolean => {
  if (!comment.lastEditedAt) return false;
  const timeBetweenPostingAndEditingMs = new Date(comment.lastEditedAt).getTime() - new Date(comment.postedAt).getTime();
  return (timeBetweenPostingAndEditingMs > EDIT_GRACE_PERIOD);
}

const CommentDateTooltip = ({comment}: {
  comment: CommentsList
}) => {
  if (markCommentAsEdited(comment) && comment.lastEditedAt) {
    return <>
      <div>Posted <ExpandedDate date={comment.postedAt} /></div>
      <div>Last edited <ExpandedDate date={comment.lastEditedAt} /></div>
    </>
  } else {
    return <ExpandedDate date={comment.postedAt} />
  }
}

export default CommentsItemDate;



